/**
 * 项目页（S06 起接真实接口）：
 *  - 项目列表 = GET /api/projects，新建 = POST /api/projects
 *  - 每行可进入该项目的「资产中心」
 *  - 保留健康检查卡，用于全链路验证「登录 → callFunction → api → REST → PG」
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Button, Card, Descriptions, Form, Input, Modal, Space, Tag, Typography, message } from "antd";
import { callApi, ApiError } from "../lib/api";
import { useProject } from "../lib/project";
import { AsyncBoundary } from "../components/AsyncBoundary";

interface HealthData {
  db: string;
  version: string;
  node: string;
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { projects, projectId, switchProject, projectLoading } = useProject();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const health = useQuery({
    queryKey: ["health"],
    queryFn: async () => (await callApi<HealthData>("/api/health")).data,
    retry: 1,
  });

  const create = useMutation({
    mutationFn: async (v: Record<string, string>) => {
      const body = Object.fromEntries(Object.entries(v).filter(([, val]) => val?.trim()));
      return (await callApi("/api/projects", { method: "POST", body })).data;
    },
    onSuccess: () => {
      message.success("项目已创建");
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (e) =>
      message.error(e instanceof ApiError ? `${e.message}（code=${e.code}）` : String(e)),
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          项目
        </Typography.Title>
        <Button type="primary" onClick={() => setOpen(true)}>
          新建项目
        </Button>
      </div>

      <Card style={{ maxWidth: 720, marginBottom: 24 }}>
        <AsyncBoundary loading={projectLoading} empty={projects.length === 0} emptyText="还没有项目，点右上角新建">
          {projects.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 4px",
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <Space>
                <Typography.Text strong>{p.name}</Typography.Text>
                {p.id === projectId && <Tag color="blue">当前</Tag>}
                {p.domain && <Typography.Text type="secondary">{p.domain}</Typography.Text>}
              </Space>
              <Space>
                {p.id !== projectId && (
                  <Button size="small" onClick={() => switchProject(p.id)}>
                    设为当前
                  </Button>
                )}
                <Button size="small" type="primary" onClick={() => navigate("/projects/" + p.id + "/assets")}>
                  资产中心
                </Button>
              </Space>
            </div>
          ))}
        </AsyncBoundary>
      </Card>

      <Card title="平台健康检查（GET /api/health）" style={{ maxWidth: 720 }}>
        <AsyncBoundary loading={health.isLoading} error={health.error} onRetry={() => health.refetch()}>
          {health.data && (
            <Descriptions column={1} size="small">
              <Descriptions.Item label="数据通道">
                {health.data.db === "ok" ? (
                  <Tag color="success">ok（PostgREST 通道连通）</Tag>
                ) : (
                  <Tag color="error">{health.data.db}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="api 版本">{health.data.version}</Descriptions.Item>
              <Descriptions.Item label="运行时">{health.data.node}</Descriptions.Item>
            </Descriptions>
          )}
        </AsyncBoundary>
      </Card>

      <Modal
        title="新建项目"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v as Record<string, string>)}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: "填写项目名称" }]}>
            <Input placeholder="例：仓储搬运" />
          </Form.Item>
          <Form.Item name="domain" label="领域（可选）">
            <Input placeholder="例：manipulation / logistics" />
          </Form.Item>
          <Form.Item name="description" label="描述（可选）">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
